import { Link } from "react-router-dom";
import { ArrowRight, Lock, Compass, UserCheck, Shield } from "lucide-react";

const BOARDROOM_IMG =
  "https://images.unsplash.com/photo-1758518730083-4c12527b6742?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjBib2FyZHJvb20lMjBleGVjdXRpdmVzfGVufDB8fHx8MTc4MzE5NDM2MHww&ixlib=rb-4.1.0&q=85";

export default function About() {
  return (
    <main data-testid="about-page" className="bg-[#040812]">
      <section className="pt-32 md:pt-40 pb-20 md:pb-24 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <span className="overline">About the Firm</span>
            <h1 className="mt-6 font-display text-white text-5xl md:text-6xl lg:text-7xl tracking-tighter font-light leading-[1.02]">
              We built Sriman Solutions to serve the{" "}
              <span className="text-red-500">enterprises</span> that can no
              longer trust generic vendors.
            </h1>
          </div>
          <div className="lg:col-span-5 text-slate-400 text-base md:text-lg leading-relaxed">
            A privately held, founder-led technology firm working exclusively
            with regulated institutions, fintechs, and premium global operators.
            We remain bootstrapped by choice — because operational autonomy is a
            promise we make to every client.
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="relative border border-white/10 overflow-hidden">
              <img
                src={BOARDROOM_IMG}
                alt="Corporate boardroom"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#040812]/70 via-transparent to-transparent" />
            </div>
          </div>
          <div className="lg:col-span-6">
            <span className="overline">Our mandate</span>
            <h2 className="mt-6 font-display text-white text-3xl md:text-4xl lg:text-5xl tracking-tighter font-light leading-tight">
              High-stakes work, delivered by people you can name.
            </h2>
            <p className="mt-6 text-slate-400 text-base md:text-lg leading-relaxed">
              We do not run junior-heavy delivery models. Every engagement is
              led by a partner who is personally accountable for the outcome,
              the compliance posture and the data handling — from first
              conversation to steady-state operation.
            </p>
            <p className="mt-4 text-slate-400 text-base md:text-lg leading-relaxed">
              This is deliberately not a hyperscale business. It is built to
              serve a small number of demanding institutions extraordinarily
              well.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <span className="overline">Operating Principles</span>
          <h2 className="mt-6 font-display text-white text-3xl md:text-4xl lg:text-5xl tracking-tighter font-light leading-tight max-w-3xl">
            Four commitments we do not compromise on.
          </h2>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                icon: Lock,
                title: "Privacy First",
                text: "Client data is compartmentalized, isolated and never used to train shared models.",
              },
              {
                icon: Shield,
                title: "Security by Design",
                text: "Zero-trust architectures, hardened by default, audited continuously.",
              },
              {
                icon: Compass,
                title: "No External Capital",
                text: "100% bootstrapped. No board, no term sheet, no misaligned incentives.",
              },
              {
                icon: UserCheck,
                title: "Founder On Every Call",
                text: "Escalation paths are personal, direct, and answered — including out of hours.",
              },
            ].map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                className="bg-[#0B132B]/50 border border-white/10 p-8 hover-lift"
              >
                <Icon size={20} className="text-red-500" strokeWidth={1.5} />
                <h3 className="mt-6 font-display text-white text-lg tracking-tight">
                  {title}
                </h3>
                <p className="mt-4 text-slate-400 text-sm leading-relaxed">
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <span className="overline">Work with us</span>
          <h2 className="mt-6 font-display text-white text-4xl md:text-5xl tracking-tighter font-light leading-tight">
            If your operation cannot afford a generic vendor,
            <br />
            we should probably talk.
          </h2>
          <Link
            to="/contact"
            data-testid="about-cta-button"
            className="mt-10 inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white font-medium px-7 py-4 rounded-sm transition-colors"
          >
            Schedule a Consultation
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </main>
  );
}
